import React from "react";
import styled from "styled-components";
import { ValidationResponse } from "../../types/validationTypes";
import { VALIDATION_ERROR_TYPE_LABELS } from "../../enums/Validation";
import { StyledTable } from "../tables/styledComponents";
import { CustomTableCell } from "../tables/CustomTableCell";
import { TableCellType, CellType } from "../../models/tableModels";
import { CompassTag, TagProps } from "../visuals/CompassTag";
import { validationErrorTypeColors } from "../../styles";

const ErrorDisplayContainer = styled.div`
  width: 100%;
  padding: 0px 32px;
`;

const TagWrapper = styled.div`
  display: inline-block;
`;

/**
 * Get label for error type
 */
const getErrorTypeLabel = (errorType: string): string => {
  return VALIDATION_ERROR_TYPE_LABELS[errorType] || errorType;
};

/**
 * Build TagProps for error type
 */
const buildErrorTypeTagProps = (errorType: string): TagProps => {
  return {
    tagKey: errorType,
    displayValue: getErrorTypeLabel(errorType),
    bg: validationErrorTypeColors.bg,
    color: validationErrorTypeColors.text,
  };
};

/**
 * Validation error column definitions
 */
const VALIDATION_ERROR_COLUMNS = [
  {
    title: "Row",
    dataIndex: "row",
    key: "row",
  },
  {
    title: "Field",
    dataIndex: "field",
    key: "field",
  },
  {
    title: "Error Type",
    dataIndex: "error_type",
    key: "error_type",
  },
  {
    title: "Message",
    dataIndex: "message",
    key: "message",
  },
  {
    title: "Expected",
    dataIndex: "expected",
    key: "expected",
  },
  {
    title: "Got",
    dataIndex: "got",
    key: "got",
  },
];

/**
 * Get cell type and value based on column key
 */
const getCellTypeAndValue = (
  key: string,
  value: CellType
): { type: TableCellType; value: CellType } => {
  switch (key) {
    case "row":
      return { type: TableCellType.NUMBER, value };
    case "error_type":
      // Return custom render for error type tag
      return {
        type: TableCellType.TEXT,
        value: getErrorTypeLabel(value as string),
      };
    default:
      return { type: TableCellType.TEXT, value: value || "-" };
  }
};

interface ValidationErrorDisplayProps {
  validationResponse: ValidationResponse;
}

/**
 * Component to display validation errors in a table format
 */
export const ValidationErrorDisplay: React.FC<ValidationErrorDisplayProps> = ({
  validationResponse,
}) => {
  const data = validationResponse.data;
  const errors = data?.errors;

  if (!errors || errors.length === 0) {
    return null;
  }

  // Transform errors to table data source
  const dataSource = errors.map((error, index) => ({
    ...error,
    key: `${error.row}-${error.field}-${index}`,
    got: error.got || "(empty)",
  }));

  return (
    <ErrorDisplayContainer>
      <StyledTable
        dataSource={dataSource}
        hasEveryCellBorderBottom={true}
        pagination={false}
        scroll={{ y: 300 }}
        size="small"
      >
        {VALIDATION_ERROR_COLUMNS.map((column) => (
          <StyledTable.Column
            key={column.key}
            dataIndex={column.dataIndex}
            title={column.title}
            render={(value: CellType): JSX.Element => {
              // Special handling for error_type to show colored tag
              if (column.key === "error_type") {
                return (
                  <TagWrapper>
                    <CompassTag {...buildErrorTypeTagProps(value as string)} />
                  </TagWrapper>
                );
              }
              const { type, value: cellValue } = getCellTypeAndValue(
                column.key,
                value
              );
              return <CustomTableCell type={type} value={cellValue} />;
            }}
          />
        ))}
      </StyledTable>
    </ErrorDisplayContainer>
  );
};
